import { useState, useEffect, useCallback, useRef } from 'react';
import {
  getStatusOffline, sincronizarDadosMestres,
  sincronizarConferencias,
  autoSincronizarMontagens,
  limparConferenciasSincronizadas,
  type OfflineMeta
} from '../services/offlineData';
import { sincronizarPendentes } from '../services/offlineQueue';

const SYNC_MONTAGENS_INTERVAL = 10 * 60 * 1000; // 10 minutos - montagens de cargas
const SYNC_RECENTE_MS = 30 * 1000; // 30 segundos - considera sync recente (evita duplicar com login)

export function useOfflineMode() {
  const [pronto, setPronto] = useState(false);
  const [meta, setMeta] = useState<OfflineMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [sincronizando, setSincronizando] = useState(false);
  const montagensIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const recarregar = useCallback(async () => {
    try {
      const status = await getStatusOffline();
      setPronto(status.pronto);
      setMeta(status.meta);
    } catch {
      setPronto(false);
      setMeta(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Sincronizar todos os dados mestres manualmente
  const sincronizar = useCallback(async () => {
    if (sincronizando) return;
    setSincronizando(true);
    try {
      const novaMeta = await sincronizarDadosMestres();
      setPronto(true);
      setMeta(novaMeta);
    } catch {
      // Silenciar - auto-sync nao deve bloquear o uso
    } finally {
      setSincronizando(false);
    }
  }, [sincronizando]);

  // Sync montagens de cargas a cada 10 minutos
  const syncMontagens = useCallback(async () => {
    if (!navigator.onLine) return;
    try {
      await autoSincronizarMontagens();
      await recarregar();
    } catch {
      // Silenciar
    }
  }, [recarregar]);

  // Sync imediata de conferencias + pendentes ao ficar online
  const handleOnline = useCallback(async () => {
    try {
      // 1. Sincronizar conferencias pendentes (criadas offline) e leituras
      await sincronizarPendentes().catch(() => {});

      // 2. Sincronizar conferencias ativas do servidor
      await sincronizarConferencias().catch(() => {});

      // 3. Limpar conferencias ja sincronizadas
      await limparConferenciasSincronizadas().catch(() => {});

      // 4. Atualizar estado
      await recarregar();
    } catch {
      // Silenciar
    }
  }, [recarregar]);

  // Carrega status ao montar
  useEffect(() => {
    recarregar();
  }, [recarregar]);

  // Sync inicial + intervalos
  useEffect(() => {
    if (navigator.onLine) {
      // Verificar se dados ja foram sincronizados recentemente (ex: pelo login)
      getStatusOffline().then((status) => {
        const syncRecente = status.meta?.preparado_em &&
          (Date.now() - new Date(status.meta.preparado_em).getTime()) < SYNC_RECENTE_MS;

        if (!syncRecente) {
          // Dados antigos ou inexistentes - sincronizar montagens
          autoSincronizarMontagens()
            .then(() => recarregar())
            .catch(() => {});
        }

        // Limpar conferencias finalizadas (leve, pode rodar sempre)
        limparConferenciasSincronizadas().catch(() => {});
      }).catch(() => {});
    }

    // Intervalo de 10 minutos para montagens de cargas
    montagensIntervalRef.current = setInterval(() => {
      if (navigator.onLine) {
        syncMontagens();
      }
    }, SYNC_MONTAGENS_INTERVAL);

    // Sync imediata ao reconectar
    window.addEventListener('online', handleOnline);

    return () => {
      if (montagensIntervalRef.current) {
        clearInterval(montagensIntervalRef.current);
      }
      window.removeEventListener('online', handleOnline);
    };
  }, [syncMontagens, handleOnline, recarregar]);

  return { pronto, meta, loading, sincronizando, recarregar, sincronizar };
}
